import dayjs from 'dayjs';

import { HttpService } from '@/data/http/http-service';
import {
  VenueDate,
  VenueDateBasic,
} from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { ProjectVenueDatesGateway } from '@/domain/project-venue-dates/gateways/project-venue-dates.gateway';

import {
  getVenueDatesDtoSchema,
  importDatesFromHeedsDtoSchema,
  importDatesFromOrfeoDtoSchema,
  venueDateDtoSchema,
} from './project-venue-dates.dto.schema';
import { VenueDateMapper } from './project-venue-dates.mapper';

export class ApiProjectVenueDatesHttpGateway implements ProjectVenueDatesGateway {
  private http = new HttpService({
    baseURL: import.meta.env.VITE_API_PROJECT,
  });

  async getAll(projectId: number): Promise<VenueDate[]> {
    const url = `/seasons/dates?seasonId=${projectId}`;
    const options = { params: { $limit: 1000 } };
    const response = await this.http.get(url, options);
    const result = getVenueDatesDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data.data.map((x) => VenueDateMapper.dtoToEntity(x));
  }

  async add(
    organisationId: number,
    projectId: number,
    date: string,
    name: string,
  ): Promise<void> {
    const url = '/seasons/dates';
    const dateObj = new Date(date);
    dateObj.setHours(16, 0, 0, 0);
    const data = {
      organisationId: organisationId,
      seasonId: projectId,
      name: name,
      startDate: dateObj.toISOString(),
    };
    const response = await this.http.post(url, data);
    if (response.status === 201) {
      return;
    } else {
      throw 'error on creation';
    }
  }

  async edit(venueDate: VenueDate): Promise<VenueDate> {
    const url = `/seasons/dates/${venueDate.id}`;
    const response = await this.http.patch(
      url,
      VenueDateMapper.entityToDto(venueDate),
    );

    if (response.status === 200) {
      const result = venueDateDtoSchema.safeParse(response.data);
      if (result.success === false) {
        console.error(result.error);
        throw result.error;
      }
      return VenueDateMapper.dtoToEntity(result.data);
    } else {
      throw 'error on creation';
    }
  }

  async delete(dateId: number): Promise<void> {
    const url = `/seasons/dates/${dateId}`;
    const response = await this.http.delete(url);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on delete';
    }
  }

  async bulkAdd(body: {
    dates: { date: string; name: string; presetId?: number }[];
    projectId: number;
    organisationId: number;
  }): Promise<{ success: boolean }> {
    const url = '/seasons/dates';
    const data = body.dates.map((date) => {
      const dateObj = new Date(date.date);
      dateObj.setHours(16, 0, 0, 0);
      return {
        organisationId: body.organisationId,
        seasonId: body.projectId,
        name: date.name,
        startDate: dateObj.toISOString(),
        properties: {
          presetId: date.presetId,
          customAvgCapacity: false,
        },
      };
    });
    const response = await this.http.post(url, data, {
      additionalHeaders: { 'X-Service-Method': 'batchInsert' },
    });
    if (response.status === 200) {
      return { success: true };
    } else {
      throw 'error on creation';
    }
  }

  async importDatesFromHeeds(
    username: string,
    password: string,
    startDate: string,
    endDate: string,
  ): Promise<VenueDateBasic[]> {
    const url = `/seasons/dates-import-heeds`;
    const data = {
      username: username,
      password: password,
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
    };
    const response = await this.http.post(url, data);
    if (response.status !== 201) {
      throw 'error on import';
    }
    const result = importDatesFromHeedsDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data;
  }

  async importDatesFromOrfeo(
    apiToken: string,
    startDate: string,
    endDate: string,
  ): Promise<VenueDateBasic[]> {
    const url = `/seasons/dates-import-orfeo`;
    const data = {
      apiToken: apiToken,
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
    };
    const response = await this.http.post(url, data);
    if (response.status !== 201) {
      throw 'error on import';
    }
    const result = importDatesFromOrfeoDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data;
  }
}
